import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X } from 'lucide-react';
import { DirhamSymbol } from 'dirham/react';

/**
 * CustomAccountSelect
 * A responsive, accessible account dropdown and mobile sheet selector.
 * Solves the missing Dirham symbol glyph (▯) bug in mobile native <select>
 * by rendering in-DOM with the official UAE Dirham vector symbol.
 */
export default function CustomAccountSelect({
  value,
  onChange,
  accounts = [],
  filter,
  placeholder = 'Select Account',
  required = false,
  disabled = false,
  style = {},
  className = '',
  includeVirtualDebt = false,
  virtualDebtLabel = "🤝 Credit to Friend's Balance / Add to Debt",
  formatMoney,
  name,
  id,
  zIndex
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Lock body scroll when modal sheet is open
  useEffect(() => {
    if (isOpen) {
      const origOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = origOverflow;
      };
    }
  }, [isOpen]);

  // Filter accounts if predicate provided
  const availableAccounts = useMemo(() => {
    let list = accounts;
    if (filter && typeof filter === 'function') {
      list = list.filter(filter);
    }
    return list;
  }, [accounts, filter]);

  // Find currently selected account
  const selectedAccount = useMemo(() => {
    if (!value || value === 'VIRTUAL_DEBT') return null;
    return availableAccounts.find(a => a.account_id === value) || accounts.find(a => a.account_id === value) || null;
  }, [value, availableAccounts, accounts]);

  // Search filtered accounts
  const displayedAccounts = useMemo(() => {
    if (!searchQuery.trim()) return availableAccounts;
    const q = searchQuery.toLowerCase().trim();
    return availableAccounts.filter(acc => {
      const nameMatch = acc.name && acc.name.toLowerCase().includes(q);
      const currMatch = acc.currency && acc.currency.toLowerCase().includes(q);
      const typeMatch = acc.type && acc.type.toLowerCase().includes(q);
      return nameMatch || currMatch || typeMatch;
    });
  }, [availableAccounts, searchQuery]);

  const handleSelect = (accId) => {
    if (disabled) return;
    setIsOpen(false);
    setSearchQuery('');
    if (onChange) {
      // Dispatch synthetic event object for standard React input handler compatibility
      onChange({
        target: {
          value: accId,
          name: name || id || ''
        }
      });
    }
  };

  const getAccountIcon = (acc) => {
    if (!acc) return '🏦';
    if (acc.type === 'CREDIT_CARD' || acc.account_type === 'CREDIT_CARD') return '💳';
    if (acc.type === 'CASH' || acc.account_type === 'CASH') return '💵';
    if (acc.type === 'INVESTMENT' || acc.account_type === 'INVESTMENT') return '📈';
    return '🏦';
  };

  const formatBalanceDisplay = (bal, currency = 'AED') => {
    const isNegative = bal < 0;
    const absVal = Math.abs(bal);
    const numParts = absVal.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    if (currency === 'AED') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontWeight: '700' }}>
          <span>{isNegative ? '-' : ''}</span>
          <DirhamSymbol size={13} style={{ display: 'inline-block', verticalAlign: '-0.1em' }} />
          <span>{numParts}</span>
        </span>
      );
    }

    if (formatMoney) {
      return formatMoney(bal, currency);
    }

    return `${isNegative ? '-' : ''}${numParts} ${currency}`;
  };

  const getDisplayBalance = (acc) => {
    if (!acc) return 0;
    return acc.type === 'CREDIT_CARD' ? -parseFloat(acc.current_balance || 0) : parseFloat(acc.current_balance || 0);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Hidden input for HTML form validation if required */}
      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{
            opacity: 0,
            width: 0,
            height: 0,
            position: 'absolute',
            pointerEvents: 'none'
          }}
          value={value || ''}
          onChange={() => {}}
          required={required}
        />
      )}

      {/* Trigger Button */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(prev => !prev)}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            setIsOpen(prev => !prev);
          }
        }}
        className={className}
        style={{
          width: '100%',
          minHeight: '46px',
          backgroundColor: 'var(--bg-input)',
          border: `1px solid ${isOpen ? 'var(--accent-cyan)' : 'var(--border-glass)'}`,
          boxShadow: isOpen ? '0 0 8px var(--accent-glow)' : 'none',
          borderRadius: '12px',
          padding: '10px 14px',
          color: 'var(--text-main)',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'var(--transition-smooth)',
          boxSizing: 'border-box',
          userSelect: 'none',
          ...style
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', flex: 1 }}>
          {value === 'VIRTUAL_DEBT' ? (
            <span style={{ fontWeight: '600', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {virtualDebtLabel}
            </span>
          ) : selectedAccount ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
              <span style={{ fontSize: '16px', flexShrink: 0 }}>{getAccountIcon(selectedAccount)}</span>
              <span style={{ fontWeight: '600', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selectedAccount.name}
              </span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>
                ({selectedAccount.currency})
              </span>
            </div>
          ) : (
            <span style={{ color: 'var(--text-muted)', fontStyle: 'normal' }}>
              {placeholder}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: '8px' }}>
          {selectedAccount && (
            <span style={{
              fontSize: '12px',
              padding: '2px 7px',
              borderRadius: '6px',
              background: getDisplayBalance(selectedAccount) >= 0 ? 'rgba(46, 213, 115, 0.12)' : 'rgba(255, 71, 87, 0.12)',
              color: getDisplayBalance(selectedAccount) >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)',
              fontWeight: '700'
            }}>
              {formatBalanceDisplay(getDisplayBalance(selectedAccount), selectedAccount.currency)}
            </span>
          )}
          <ChevronDown
            size={16}
            style={{
              color: 'var(--text-muted)',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          />
        </div>
      </div>

      {/* Portal Modal / Bottom Sheet */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="account-select-backdrop"
          style={zIndex ? { zIndex } : undefined}
          onClick={() => { setIsOpen(false); setSearchQuery(''); }}
        >
          <div
            className="account-select-panel"
            onClick={e => e.stopPropagation()}
          >
            <div className="account-select-pill-handle" />

            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>🏦</span>
                <span style={{ fontWeight: 'bold', fontSize: '15px', color: 'var(--text-main)' }}>
                  {placeholder || 'Select Account'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => { setIsOpen(false); setSearchQuery(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '4px 6px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  lineHeight: 1
                }}
              >
                ✕
              </button>
            </div>

            {/* Quick Search */}
            {availableAccounts.length > 4 && (
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  placeholder="Search account name or currency..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '9px 12px 9px 36px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid var(--border-glass)',
                    color: 'var(--text-main)',
                    fontSize: '16px', // Prevents iOS Safari auto-zoom
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
              </div>
            )}

            {/* Account Options List */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              overflowY: 'auto',
              maxHeight: '55vh',
              paddingRight: '2px',
              WebkitOverflowScrolling: 'touch'
            }}>
              {/* Virtual Debt Option */}
              {includeVirtualDebt && (
                <div
                  role="button"
                  onClick={() => handleSelect('VIRTUAL_DEBT')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    background: value === 'VIRTUAL_DEBT' ? 'rgba(0, 245, 212, 0.12)' : 'rgba(255, 255, 255, 0.04)',
                    border: `1px solid ${value === 'VIRTUAL_DEBT' ? 'var(--accent-cyan)' : 'rgba(255, 255, 255, 0.08)'}`,
                    cursor: 'pointer',
                    transition: 'var(--transition-smooth)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '18px' }}>🤝</span>
                    <div>
                      <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-main)', display: 'block' }}>
                        {virtualDebtLabel}
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        Does not affect personal bank balance
                      </span>
                    </div>
                  </div>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: value === 'VIRTUAL_DEBT' ? '2px solid var(--accent-cyan)' : '1.5px solid var(--border-glass)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {value === 'VIRTUAL_DEBT' && (
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent-cyan)' }} />
                    )}
                  </div>
                </div>
              )}

              {/* Account Items */}
              {displayedAccounts.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px', padding: '20px 0' }}>
                  No accounts found matching your search.
                </p>
              ) : (
                displayedAccounts.map(acc => {
                  const isSelected = value === acc.account_id;
                  const displayBal = getDisplayBalance(acc);
                  const isDisabled = acc.is_active === false || acc.is_active === 0 || acc.is_active === '0';

                  return (
                    <div
                      key={acc.account_id}
                      role="button"
                      onClick={() => handleSelect(acc.account_id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        background: isSelected ? 'rgba(0, 245, 212, 0.1)' : 'rgba(255, 255, 255, 0.04)',
                        border: `1px solid ${isSelected ? 'var(--accent-cyan)' : 'rgba(255, 255, 255, 0.08)'}`,
                        cursor: 'pointer',
                        transition: 'var(--transition-smooth)',
                        opacity: isDisabled ? 0.6 : 1
                      }}
                    >
                      {/* Left: Icon & Account Info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0, paddingRight: '8px' }}>
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          background: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          flexShrink: 0
                        }}>
                          {getAccountIcon(acc)}
                        </div>

                        <div style={{ minWidth: 0, flex: 1 }}>
                          <span style={{
                            fontWeight: '600',
                            fontSize: '14px',
                            color: 'var(--text-main)',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {acc.name}
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            {acc.type} • {acc.currency} {isDisabled ? '(Disabled)' : ''}
                          </span>
                        </div>
                      </div>

                      {/* Right: Balance & Radio Button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: displayBal >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)',
                            display: 'block'
                          }}>
                            {formatBalanceDisplay(displayBal, acc.currency)}
                          </span>
                        </div>

                        <div style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          border: isSelected ? '2px solid var(--accent-cyan)' : '1.5px solid var(--border-glass)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          {isSelected && (
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent-cyan)' }} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
